import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridColDef } from "@mui/x-data-grid";

import {
  AppDataTable,
  AppPageHeader,
  AppSearchField,
  AppToolbar,
} from "../../../components/common";
import maintenanceService from "../maintenance/api/maintenance.service";
import { HubOption } from "../maintenance/types";
import vehicleService from "../../../services/vehicleService";
import { useAuth } from "../../../contexts/AuthContext";

import useMaintenanceAssignments from "./useMaintenanceAssignments";
import { MaintenanceAssignment, MaintenanceRole } from "./types";

const roleOptions: Array<{
  label: string;
  value: MaintenanceRole | "";
}> = [
  { label: "None", value: "" },
  { label: "Super Admin", value: "SUPER_ADMIN" },
  { label: "AME", value: "AME" },
  { label: "Technician", value: "TECHNICIAN" },
];

const roleColor = (role: MaintenanceAssignment["maintenanceRole"]) => {
  if (role === "SUPER_ADMIN") return "secondary";
  if (role === "AME") return "primary";
  if (role === "TECHNICIAN") return "success";
  return "default";
};

const MaintenanceAssignmentsPage = () => {
  const { user } = useAuth();
  const {
    items,
    pagination,
    filters,
    loading,
    error,
    refresh,
    updateFilters,
    assignRole,
  } = useMaintenanceAssignments();
  const [hubs, setHubs] = useState<HubOption[]>([]);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const [savingEmployeeId, setSavingEmployeeId] = useState<string | null>(null);
  const isReadOnlyPlatformViewer = useMemo(() => {
    const roles = (user?.roles || []).map((role) =>
      role.name.trim().toUpperCase().replace(/[^A-Z0-9]/g, "_")
    );
    const isPlatformSuperAdmin = roles.some((role) =>
      ["SUPER_ADMIN", "SUPERADMIN", "SYSTEM_ADMIN", "SYSTEM_ADMINISTRATOR"].includes(role)
    );

    return (
      !isPlatformSuperAdmin &&
      roles.some((role) => ["IT_ADMIN", "EV_OPERATIONS_MANAGER"].includes(role))
    );
  }, [user?.roles]);
  const isReadOnly = isReadOnlyPlatformViewer || pagination.access?.canManage === false;

  useEffect(() => {
    maintenanceService
      .getHubs()
      .then(setHubs)
      .catch(() => setHubs([]));
  }, []);

  useEffect(() => {
    vehicleService
      .getOperationalCities()
      .then((response) =>
        setCities(
          (response?.data || [])
            .filter((city: { id?: string; name?: string }) => city.id && city.name)
            .map((city: { id: string; name: string; displayName?: string }) => ({
              id: city.id,
              name: city.displayName || city.name,
            }))
        )
      )
      .catch(() => setCities([]));
  }, []);

  const departments = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((item) => {
      if (item.departmentId && item.departmentName !== "-") {
        map.set(item.departmentId, item.departmentName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  const summary = useMemo(
    () => ({
      total: pagination.total,
      superAdmins: items.filter((item) => item.maintenanceRole === "SUPER_ADMIN")
        .length,
      ames: items.filter((item) => item.maintenanceRole === "AME").length,
      technicians: items.filter((item) => item.maintenanceRole === "TECHNICIAN")
        .length,
      unassigned: items.filter((item) => !item.maintenanceRole).length,
    }),
    [items, pagination.total]
  );

  const handleRoleChange = async (
    employeeId: string,
    maintenanceRole: MaintenanceRole | "",
    cityId?: string | null
  ) => {
    if (isReadOnly) return;

    try {
      setSavingEmployeeId(employeeId);
      await assignRole(employeeId, maintenanceRole || null, cityId);
    } finally {
      setSavingEmployeeId(null);
    }
  };

  const columns: GridColDef<MaintenanceAssignment>[] = [
    {
      field: "employeeCode",
      headerName: "Employee ID",
      minWidth: 150,
      flex: 0.8,
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      minWidth: 220,
      flex: 1.2,
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 140,
      flex: 0.8,
    },
    {
      field: "departmentName",
      headerName: "Department",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "designation",
      headerName: "Current Designation",
      minWidth: 190,
      flex: 1,
    },
    {
      field: "hubName",
      headerName: "Hub",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "employeeStatus",
      headerName: "Status",
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === "ACTIVE" ? "success" : "default"}
        />
      ),
    },
    {
      field: "maintenanceRole",
      headerName: "Maintenance Role",
      minWidth: 180,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || "None"}
          color={roleColor(params.value)}
          variant={params.value ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "cityId",
      headerName: "Operating City",
      minWidth: 180,
      renderCell: (params) =>
        cities.find((city) => city.id === params.value)?.name ||
        (params.value ? "Assigned city" : "Not assigned"),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 350,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            size="small"
            label="City"
            value={params.row.cityId || ""}
            disabled={
              isReadOnly ||
              savingEmployeeId === params.row.employeeId ||
              params.row.maintenanceRole === "SUPER_ADMIN"
            }
            onChange={(event) =>
              handleRoleChange(
                params.row.employeeId,
                params.row.maintenanceRole || "",
                event.target.value || null
              )
            }
            sx={{ minWidth: 145 }}
          >
            <MenuItem value="">Select city</MenuItem>
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Role"
            value={params.row.maintenanceRole || ""}
            disabled={isReadOnly || savingEmployeeId === params.row.employeeId}
            onChange={(event) =>
              handleRoleChange(
                params.row.employeeId,
                event.target.value as MaintenanceRole | "",
                params.row.cityId
              )
            }
            sx={{ minWidth: 165 }}
          >
            {roleOptions.map((role) => (
              <MenuItem key={role.label} value={role.value}>
                {params.row.maintenanceRole && !role.value
                  ? "Remove Role"
                  : role.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "calc(100vh - 96px)",
        borderRadius: 3,
        bgcolor: "background.paper",
      }}
    >
      <AppPageHeader
        title="Spare Parts Role Assignments"
        subtitle={
          isReadOnly
            ? pagination.access?.cityId
              ? "View Spare Parts role assignments for your assigned city."
              : "View Spare Parts role assignments."
            : "Assign Spare Parts roles to existing Employee Management records."
        }
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
        >
          {error} {items.length > 0 ? "Previously loaded assignments are still visible." : "Saved roles have not been removed."}
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary">Employees</Typography>
            <Typography variant="h4" fontWeight={800}>
              {summary.total}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary">Super Admins</Typography>
            <Typography variant="h4" fontWeight={800}>
              {summary.superAdmins}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary">AME</Typography>
            <Typography variant="h4" fontWeight={800}>
              {summary.ames}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary">Technicians</Typography>
            <Typography variant="h4" fontWeight={800}>
              {summary.technicians}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary">Unassigned</Typography>
            <Typography variant="h4" fontWeight={800}>
              {summary.unassigned}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <AppToolbar
        left={
          <>
            <AppSearchField
              value={filters.search || ""}
              placeholder="Search Employee..."
              onChange={(search) => updateFilters({ search, page: 1 })}
            />
            <TextField
              select
              size="small"
              label="Hub"
              value={filters.hubId || ""}
              onChange={(event) =>
                updateFilters({ hubId: event.target.value, page: 1 })
              }
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Hubs</MenuItem>
              {hubs.map((hub) => (
                <MenuItem key={hub.id} value={hub.id}>
                  {hub.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Department"
              value={filters.departmentId || ""}
              onChange={(event) =>
                updateFilters({ departmentId: event.target.value, page: 1 })
              }
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Departments</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Maintenance Role"
              value={filters.maintenanceRole || ""}
              onChange={(event) =>
                updateFilters({
                  maintenanceRole: event.target.value as typeof filters.maintenanceRole,
                  page: 1,
                })
              }
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="NONE">None</MenuItem>
              <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
              <MenuItem value="AME">AME</MenuItem>
              <MenuItem value="TECHNICIAN">Technician</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={filters.status || ""}
              onChange={(event) =>
                updateFilters({
                  status: event.target.value as typeof filters.status,
                  page: 1,
                })
              }
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </>
        }
        right={
          <Button startIcon={<RefreshIcon />} onClick={refresh}>
            Refresh
          </Button>
        }
      />

      <AppDataTable
        rows={items}
        columns={columns}
        loading={loading}
        page={(filters.page || 1) - 1}
        pageSize={filters.limit || 20}
        rowCount={pagination.total}
        getRowId={(row) => row.employeeId}
        onPageChange={(page) => updateFilters({ page: page + 1 })}
        onPageSizeChange={(limit) => updateFilters({ page: 1, limit })}
      />
    </Box>
  );
};

export default MaintenanceAssignmentsPage;
